import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { admin, moderation, files as filesApi } from '../services/api';
import { toast } from '../hooks/use-toast';
import { ShieldAlert, UnlockIcon, Loader2, Lock, Calendar, Mail, Flag, Check, PartyPopper, BarChart3, X, ClipboardList, DollarSign, Building2, Crown, Search, UserPlus, Send, Repeat2, Gavel, Edit3, Users, Key, Megaphone, CalendarDays, Heart, Newspaper, Trash2, Layout, MonitorSmartphone, Zap, Tag } from 'lucide-react';
import ClampWithMore from '../components/ClampWithMore';
import AdminAuctionsPanel from '../components/AdminAuctionsPanel';
import AdminUsersPanel from '../components/AdminUsersPanel';
import AdminPsaPanel from '../components/AdminPsaPanel';
import AdminContentPanel from '../components/AdminContentPanel';
import AdminDatingReportsPanel from '../components/AdminDatingReportsPanel';
import AdminDatingVerificationsPanel from '../components/AdminDatingVerificationsPanel';
import AdminSiteEditorPanel from '../components/AdminSiteEditorPanel';
import AdminPopupPanel from '../components/AdminPopupPanel';
import AdminBlackoutPanel from '../components/AdminBlackoutPanel';
import AdminPlanPricesPanel from '../components/AdminPlanPricesPanel';
import AdminSliderFormsPanel from '../components/AdminSliderFormsPanel';
import AdminSalesPanel from '../components/AdminSalesPanel';
import AdminContactsPanel from '../components/AdminContactsPanel';
import AdminNewsSourcesPanel from '../components/AdminNewsSourcesPanel';
import AdminWelcomeEditor from '../components/AdminWelcomeEditor';
import AdminReportsPanel from '../components/AdminReportsPanel';
import AdminCommunityReportsPanel from '../components/AdminCommunityReportsPanel';
import AdminNewsletterPanel from '../components/AdminNewsletterPanel';

const TABS = [
  { key: 'moderation', label: 'Moderation queue', icon: Flag },
  { key: 'auctions', label: 'Auctions', icon: Gavel },
  { key: 'tiers', label: 'Business tiers', icon: Crown },
  { key: 'users', label: 'Users & passwords', icon: Key },
  { key: 'site-editor', label: 'Public site editor', icon: Layout },
  { key: 'popup', label: 'Pop-up Manager', icon: MonitorSmartphone },
  { key: 'blackout', label: 'Blackout Game', icon: Zap },
  { key: 'plan-prices', label: 'Stripe Prices', icon: Tag },
  { key: 'sales', label: 'Sales Tracking', icon: DollarSign },
  { key: 'contacts', label: 'Contacts', icon: Users },
  { key: 'slider-forms', label: 'Slider Signup Forms', icon: Mail },
  { key: 'welcome', label: 'Welcome Messages', icon: PartyPopper },
  { key: 'reports', label: 'Reports', icon: BarChart3 },
  { key: 'newsletter', label: 'Newsletter', icon: Mail },
  { key: 'newsletter-contacts', label: 'Newsletter Contacts', icon: Users },
  { key: 'community-reports', label: 'Community reports', icon: Flag },
  { key: 'events', label: 'Events', icon: CalendarDays },
  { key: 'dating', label: 'Dating', icon: Heart },
  { key: 'dating-reports', label: 'Dating reports', icon: Flag },
  { key: 'dating-verifications', label: 'Dating verifications', icon: Check },
  { key: 'news', label: 'News', icon: Newspaper },
  { key: 'psa', label: 'PSA', icon: Megaphone },
  { key: 'frozen', label: 'Flagged users', icon: ShieldAlert },
];

// Schema config for the 3 God-Mode CRUD panels. Each `fields` entry drives
// both the create/edit modal and the coerce-on-save logic.
const EVENTS_SCHEMA = {
  title: 'Events',
  testid: 'admin-events',
  emptyIcon: CalendarDays,
  emptyMessage: 'No events yet',
  fields: [
    { key: 'title', label: 'Title', type: 'text', required: true, span: 2 },
    { key: 'date', label: 'Date', type: 'text', placeholder: 'Apr 12, 2026' },
    { key: 'time', label: 'Time', type: 'text', placeholder: '7:00 PM' },
    { key: 'city', label: 'City', type: 'text', placeholder: 'Atlanta, GA' },
    { key: 'location', label: 'Location / venue', type: 'text' },
    { key: 'address', label: 'Street address', type: 'text', span: 2 },
    { key: 'price', label: 'Price (USD)', type: 'number' },
    { key: 'capacity', label: 'Capacity (0 = unlimited)', type: 'number', placeholder: '0' },
    { key: 'image', label: 'Cover photo', type: 'imageUpload', bucket: 'business', aspect: 'video', hint: 'Displayed as the event banner on every event card. 16:9 works best.', span: 2 },
    { key: 'description', label: 'Description', type: 'textarea', span: 2 },
  ],
  renderRow: (item, actions) => (
    <>
      <td className="px-3 py-2 w-20">
        {item.image ? <img src={filesApi.urlFor(item.image)} alt="" className="w-14 h-14 rounded-md object-cover" /> : <div className="w-14 h-14 rounded-md bg-[#1a2a20] border border-[#153b23]" />}
      </td>
      <td className="px-3 py-2">
        <div className="font-semibold text-sm text-white">{item.title}</div>
        <div className="text-xs text-gray-400">{item.date || '—'} {item.time ? `· ${item.time}` : ''} · {item.city || '—'}</div>
      </td>
      <td className="px-3 py-2 text-xs text-gray-400 whitespace-nowrap">${(item.price || 0).toFixed(2)} · {item.attending || 0} RSVP</td>
      <td className="px-3 py-2 text-right">{actions(item)}</td>
    </>
  ),
};

const DATING_SCHEMA = {
  title: 'Profiles',
  testid: 'admin-dating',
  emptyIcon: Heart,
  emptyMessage: 'No dating profiles yet',
  fields: [
    { key: 'name', label: 'Name', type: 'text', required: true },
    { key: 'age', label: 'Age', type: 'number' },
    { key: 'city', label: 'City', type: 'text' },
    { key: 'image', label: 'Profile photo', type: 'imageUpload', bucket: 'business', aspect: 'square', hint: 'Shown as the profile avatar. Square works best.', span: 2 },
    { key: 'interests', label: 'Interests (comma-separated)', type: 'csv', hint: 'e.g. Music, Tech, Fitness', span: 2 },
    { key: 'bio', label: 'Bio', type: 'textarea', span: 2 },
  ],
  renderRow: (item, actions) => (
    <>
      <td className="px-3 py-2 w-20">
        {item.image ? <img src={filesApi.urlFor(item.image)} alt="" className="w-14 h-14 rounded-full object-cover" /> : <div className="w-14 h-14 rounded-full bg-[#1a2a20] border border-[#153b23]" />}
      </td>
      <td className="px-3 py-2">
        <div className="font-semibold text-sm text-white">{item.name}{item.age ? `, ${item.age}` : ''}</div>
        <div className="text-xs text-gray-400">{item.city || '—'}</div>
        <div className="text-[11px] text-gray-500 line-clamp-1">{Array.isArray(item.interests) ? item.interests.join(' · ') : ''}</div>
      </td>
      <td className="px-3 py-2 text-right">{actions(item)}</td>
    </>
  ),
};

const NEWS_SCHEMA = {
  title: 'Articles',
  testid: 'admin-news',
  emptyIcon: Newspaper,
  emptyMessage: 'No news articles yet',
  fields: [
    { key: 'title', label: 'Headline', type: 'text', required: true, span: 2 },
    { key: 'source', label: 'Source', type: 'text', placeholder: 'PBB, BlackEnterprise…' },
    { key: 'time', label: 'Display time', type: 'text', placeholder: '3h ago' },
    { key: 'link', label: 'Link URL', type: 'text', span: 2 },
    { key: 'image', label: 'Cover photo', type: 'imageUpload', bucket: 'business', aspect: 'video', hint: 'Displayed on the News Feed card. 16:9 works best.', span: 2 },
    { key: 'summary', label: 'Summary', type: 'textarea', span: 2 },
  ],
  renderRow: (item, actions) => (
    <>
      <td className="px-3 py-2 w-20">
        {item.image ? <img src={filesApi.urlFor(item.image)} alt="" className="w-14 h-14 rounded-md object-cover" /> : <div className="w-14 h-14 rounded-md bg-[#1a2a20] border border-[#153b23]" />}
      </td>
      <td className="px-3 py-2">
        <div className="font-semibold text-sm text-white line-clamp-2">{item.title}</div>
        <div className="text-xs text-gray-400">{item.source || 'PBB'} · {item.time || '—'}</div>
      </td>
      <td className="px-3 py-2 text-right">{actions(item)}</td>
    </>
  ),
};

const PACKAGE_CHOICES = [
  { key: 'basic', label: 'Category Listing ($3/mo)', months: 1 },
  { key: 'standard', label: 'Business Growth ($36/3mo)', months: 3 },
  { key: 'featured', label: 'Full Growth Campaign ($120/3mo)', months: 3 },
];

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState('moderation');
  const [rows, setRows] = useState([]);
  const [queue, setQueue] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [bizQuery, setBizQuery] = useState('');
  const [tierModal, setTierModal] = useState(null); // { biz, package, months }
  const [transferModal, setTransferModal] = useState(null); // { biz, email, matches, chosen }
  const [editBizModal, setEditBizModal] = useState(null);   // full business edit
  const [deleteBizModal, setDeleteBizModal] = useState(null); // { biz, typed }
  // Bulk delete — Set of business ids currently checked, plus a modal that
  // pops when the admin clicks "Delete N selected". Type-to-confirm is
  // scaled to the number of rows so accidents are impossible.
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [bulkDeleteModal, setBulkDeleteModal] = useState(null); // { count, typed, progress }
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [users, mod, biz] = await Promise.all([
        admin.frozenUsers().catch(() => []),
        moderation.queue().catch(() => []),
        admin.businesses().catch(() => []),
      ]);
      setRows(users);
      setQueue(mod);
      setBusinesses(biz);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (user?.isAdmin) load(); else setLoading(false); }, [user]);

  // Auto-refresh the businesses list whenever the admin lands on the "tiers"
  // tab, so a business that was just added in another window (e.g. by the
  // owner on their /manage-listings page) appears without a manual refresh.
  useEffect(() => {
    if (!user?.isAdmin || tab !== 'tiers') return;
    (async () => {
      try {
        const list = await admin.businesses(bizQuery.trim());
        setBusinesses(list);
      } catch (_e) { /* handled by the visible Refresh button */ }
    })();
    // Intentionally omit bizQuery — we only refresh on tab change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, user]);

  if (!user?.isAdmin) {
    return (
      <div className="max-w-xl mx-auto pbb-card p-8 text-center">
        <Lock className="mx-auto text-green-400" size={26} />
        <div className="mt-3 font-semibold">Admin Access Only</div>
        <p className="text-sm text-gray-400 mt-1">Contact the PBB team to be granted admin access.</p>
      </div>
    );
  }

  const unfreeze = async (uid) => {
    setBusy(uid + ':unfreeze');
    try { await admin.unfreeze(uid); toast({ title: 'Freeze lifted' }); await load(); }
    catch (err) { toast({ title: 'Failed', description: err?.response?.data?.detail || 'Try again' }); }
    finally { setBusy(null); }
  };

  const extend = async (uid) => {
    const days = parseInt(window.prompt('Extend freeze by how many days?', '7') || '0', 10);
    if (!days || days <= 0) return;
    setBusy(uid + ':extend');
    try { await admin.extendFreeze(uid, days); toast({ title: `Extended by ${days} days` }); await load(); }
    catch (err) { toast({ title: 'Failed', description: err?.response?.data?.detail || 'Try again' }); }
    finally { setBusy(null); }
  };

  const approveListing = async (aid) => {
    setBusy(aid + ':approve');
    try {
      await moderation.approve(aid);
      toast({ title: 'Listing approved', description: 'Donor was notified and +100 pts awarded if not already given.' });
      await load();
    } catch (err) { toast({ title: 'Failed', description: err?.response?.data?.detail || 'Try again' }); }
    finally { setBusy(null); }
  };

  const rejectListing = async (aid) => {
    const reason = window.prompt('Rejection reason (visible to donor):') || '';
    if (!reason.trim()) return;
    setBusy(aid + ':reject');
    try {
      await moderation.reject(aid, reason.trim());
      toast({ title: 'Listing rejected', description: 'Donor was notified.' });
      await load();
    } catch (err) { toast({ title: 'Failed', description: err?.response?.data?.detail || 'Try again' }); }
    finally { setBusy(null); }
  };

  const overridePrice = async (aid) => {
    const val = window.prompt('New starting price in points (leave blank to skip):', '');
    if (!val) return;
    const n = parseInt(val, 10);
    if (!n || n <= 0) return;
    setBusy(aid + ':price');
    try {
      await moderation.overridePricing(aid, n, null);
      toast({ title: 'Pricing updated' });
      await load();
    } catch (err) { toast({ title: 'Failed', description: err?.response?.data?.detail || 'Try again' }); }
    finally { setBusy(null); }
  };

  const overrideDelivery = async (aid) => {
    const val = window.prompt('New delivery fee in USD (0 to disable):', '');
    if (val === null || val === '') return;
    const n = parseFloat(val);
    if (Number.isNaN(n) || n < 0) return;
    setBusy(aid + ':delivery');
    try {
      await moderation.overrideDelivery(aid, n);
      toast({ title: 'Delivery fee updated', description: `Set to $${n.toFixed(2)}` });
      await load();
    } catch (err) { toast({ title: 'Failed', description: err?.response?.data?.detail || 'Try again' }); }
    finally { setBusy(null); }
  };

  const searchBusinesses = async () => {
    setBusy('biz:search');
    try {
      const list = await admin.businesses(bizQuery.trim());
      setBusinesses(list);
    } catch (err) {
      toast({ title: 'Search failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setBusy(null); }
  };

  const nudgePending = async () => {
    if (!window.confirm('Send an onboarding reminder email to every business owner who signed up but hasn\'t created a listing yet? (7-day cooldown applies — no one gets emailed twice this week.)')) return;
    setBusy('biz:nudge');
    try {
      const r = await admin.nudgePendingOwners(7);
      const parts = [`${r.sent} sent`];
      if (r.skipped) parts.push(`${r.skipped} skipped`);
      if (r.errors) parts.push(`${r.errors} errored`);
      toast({ title: 'Nudge complete', description: parts.join(' · ') });
    } catch (err) {
      toast({ title: 'Nudge failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setBusy(null); }
  };

  const submitTier = async () => {
    if (!tierModal?.biz || !tierModal?.package) return;
    setBusy('tier:submit');
    try {
      await admin.grantPackage(tierModal.biz.id, tierModal.package, tierModal.months || null);
      toast({ title: 'Tier granted', description: `${tierModal.biz.name} → ${tierModal.package.toUpperCase()}` });
      setTierModal(null);
      await load();
    } catch (err) {
      toast({ title: 'Grant failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setBusy(null); }
  };

  const lookupTransferUser = async () => {
    if (!transferModal?.email?.trim()) return;
    setBusy('transfer:lookup');
    try {
      const rows = await admin.userLookup(transferModal.email.trim());
      setTransferModal((prev) => ({ ...prev, matches: rows, chosen: rows[0] || null }));
      if (rows.length === 0) toast({ title: 'No match', description: 'That email is not registered yet — ask them to sign up first.' });
    } catch (err) {
      toast({ title: 'Lookup failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setBusy(null); }
  };

  const submitTransfer = async () => {
    if (!transferModal?.biz || !transferModal?.chosen) return;
    setBusy('transfer:submit');
    try {
      await admin.transferBusiness(transferModal.biz.id, { new_owner_id: transferModal.chosen.id });
      toast({ title: 'Ownership transferred',
        description: `${transferModal.biz.name} → ${transferModal.chosen.email}` });
      setTransferModal(null);
      await load();
    } catch (err) {
      toast({ title: 'Transfer failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setBusy(null); }
  };

  const submitEditBiz = async () => {
    if (!editBizModal) return;
    const b = editBizModal;
    if (!b.name || !b.name.trim()) {
      toast({ title: 'Business name required' });
      return;
    }
    setBusy('editbiz:submit');
    const patch = {
      name: b.name?.trim(), category: b.category, address: b.address?.trim(),
      city: b.city?.trim(), state: b.state?.trim()?.toUpperCase(), zip: b.zip?.trim(),
      phone: b.phone?.trim(), email: b.email?.trim(),
      description: b.description || '', coupon: b.coupon || '',
      points: Number(b.points || 0), tier: b.tier || 'Basic',
      package: b.package || 'basic',
      packageExpiresAt: b.packageExpiresAt || null,
    };
    try {
      const api = await import('../services/api');
      if (b.pendingOwnerId) {
        // Creating a fresh listing on behalf of a signed-up business owner.
        // POST /admin/businesses/create-for-owner creates + assigns ownership.
        await api.admin.createBusinessForOwner(b.pendingOwnerId, patch);
        toast({ title: 'Listing created', description: `${patch.name} → assigned to owner` });
      } else {
        // The admin-bypass on PUT /my-business/{bid} lets us patch any listing.
        await api.owner.updateBusiness(b.id, patch);
        toast({ title: 'Listing updated', description: `${patch.name}` });
      }
      setEditBizModal(null);
      await load();
    } catch (err) {
      const raw = err?.response?.data?.detail;
      const description = typeof raw === 'string' ? raw : (raw?.message || 'Try again');
      toast({ title: b.pendingOwnerId ? 'Create failed' : 'Update failed', description });
    } finally { setBusy(null); }
  };

  // Delete flow — DELETE /api/my-business/{bid} via admin bypass. Type-to-
  // confirm dialog protects against accidental clicks; nothing fires unless
  // the admin types the exact business name.
  const submitDeleteBiz = async () => {
    if (!deleteBizModal?.biz) return;
    const b = deleteBizModal.biz;
    if ((deleteBizModal.typed || '').trim() !== (b.name || '').trim()) {
      toast({ title: 'Type the business name exactly to confirm' });
      return;
    }
    setBusy('delbiz:submit');
    try {
      const api = await import('../services/api');
      await api.admin.deleteBusiness(b.id);
      toast({ title: 'Listing deleted', description: b.name });
      setDeleteBizModal(null);
      await load();
    } catch (err) {
      const raw = err?.response?.data?.detail;
      const description = typeof raw === 'string' ? raw : (raw?.message || 'Try again');
      toast({ title: 'Delete failed', description });
    } finally { setBusy(null); }
  };

  // Bulk delete — iterates selected rows and calls DELETE for each. Skips
  // pending-owner placeholders because they aren't real listings.
  const submitBulkDelete = async () => {
    const ids = [...bulkSelected].filter((id) => !id.startsWith('pending-'));
    if (!ids.length || !bulkDeleteModal) return;
    const expected = `DELETE ${ids.length}`;
    if ((bulkDeleteModal.typed || '').trim().toUpperCase() !== expected) {
      toast({ title: `Type "${expected}" exactly to confirm` });
      return;
    }
    setBusy('bulkdel:submit');
    setBulkDeleteModal({ ...bulkDeleteModal, progress: 0 });
    let ok = 0; let fail = 0;
    try {
      const api = await import('../services/api');
      for (const id of ids) {
        try { await api.admin.deleteBusiness(id); ok++; }
        catch { fail++; }
        setBulkDeleteModal((prev) => prev ? { ...prev, progress: ok + fail } : prev);
      }
      toast({
        title: fail ? `${ok} deleted · ${fail} failed` : `${ok} listing${ok !== 1 ? 's' : ''} deleted`,
        description: fail ? 'Check the console for the rows that couldn\'t be removed.' : undefined,
      });
      setBulkDeleteModal(null);
      setBulkSelected(new Set());
      await load();
    } catch (err) {
      toast({ title: 'Bulk delete failed', description: err?.message || 'Try again' });
    } finally { setBusy(null); }
  };

  // Which rows are eligible for bulk delete (excludes pending owners since
  // they aren't real listings).
  const bulkEligibleIds = () =>
    (filteredBusinesses || []).filter((b) => !b.pendingOwner && b.id && !String(b.id).startsWith('pending-'))
                                 .map((b) => b.id);
  const allEligibleSelected = () => {
    const ids = bulkEligibleIds();
    return ids.length > 0 && ids.every((id) => bulkSelected.has(id));
  };
  const toggleSelectAll = () => {
    const ids = bulkEligibleIds();
    if (allEligibleSelected()) {
      const next = new Set(bulkSelected);
      ids.forEach((id) => next.delete(id));
      setBulkSelected(next);
    } else {
      const next = new Set(bulkSelected);
      ids.forEach((id) => next.add(id));
      setBulkSelected(next);
    }
  };
  const toggleOne = (id) => {
    const next = new Set(bulkSelected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setBulkSelected(next);
  };

  const filteredBusinesses = bizQuery.trim()
    ? businesses.filter((b) => (b.name || '').toLowerCase().includes(bizQuery.trim().toLowerCase())
        || (b.category || '').toLowerCase().includes(bizQuery.trim().toLowerCase())
        || (b.ownerEmail || '').toLowerCase().includes(bizQuery.trim().toLowerCase()))
    : businesses;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><ShieldAlert className="text-green-400" size={24} /> Admin</h1>
          <p className="text-gray-400 text-sm">Manage moderation queue, flagged listings and frozen users.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a href="/admin/auto-responder" data-testid="link-admin-auto-responder"
             className="pbb-btn-green inline-flex items-center gap-2 max-w-max"><ClipboardList size={14}/> Auto-Responder</a>
          <a href="/admin/loyalty" data-testid="link-admin-loyalty" className="pbb-btn-green inline-flex items-center gap-2 max-w-max"><ClipboardList size={14}/> Loyalty & Events</a>
        </div>
      </div>

      <div className="flex flex-wrap gap-x-1 gap-y-0 border-b border-[#1f1f1f]">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} data-testid={`admin-tab-${key}`} onClick={() => setTab(key)}
            className={`inline-flex items-center gap-2 px-3 py-2 text-sm border-b-2 transition-colors ${tab === key ? 'border-green-500 text-green-400' : 'border-transparent text-gray-400 hover:text-white'}`}>
            <Icon size={14} /> {label}
            {key === 'moderation' && queue.length > 0 && (
              <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-orange-500/20 text-orange-400 border border-orange-500/40">{queue.length}</span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-400"><Loader2 className="animate-spin" size={16} /> Loading…</div>
      ) : tab === 'moderation' ? (
        queue.length === 0 ? (
          <div className="pbb-card p-10 text-center">
            <ClipboardList className="mx-auto text-green-400" size={26} />
            <div className="mt-3 font-semibold">Moderation queue is clear</div>
            <p className="text-sm text-gray-400 mt-1">No flagged or pending listings need attention.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {[...queue].sort((a, b) => (b.flags?.length || 0) - (a.flags?.length || 0)).map((it) => (
              <div key={it.id} data-testid={`mod-item-${it.id}`} className="pbb-card p-4 flex gap-4 flex-wrap sm:flex-nowrap">
                {it.image && <img src={filesApi.urlFor(it.image)} alt={it.title} className="w-28 h-28 rounded-lg object-cover shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold">{it.title}</div>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${it.status === 'flagged' ? 'bg-orange-500/15 text-orange-400 border-orange-500/40' : 'bg-amber-500/15 text-amber-400 border-amber-500/40'}`}>{it.status.replace('_', ' ')}</span>
                    {(it.flags?.length || 0) > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-orange-400"><Flag size={10} /> {it.flags.length} flag{it.flags.length > 1 ? 's' : ''}</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">Donor: {it.donorName || '—'} · Est. value ${(it.estimatedValue || 0).toFixed(0)} · Starting {it.startingPrice} pts · Deal Bin ${(it.dealBinPrice || 0).toFixed(2)}</div>
                  <ClampWithMore text={it.description} lines={3} className="text-xs text-gray-300 mt-2" testId={`admin-mod-desc-${it.id}`} />
                  {(it.flags || []).length > 0 && (
                    <div className="mt-2 text-[11px] text-orange-300 space-y-0.5">
                      {(it.flags || []).slice(0, 3).map((f, i) => (
                        <div key={i}>• <span className="text-white/70">{f.reason}</span>{f.detail ? ` — ${f.detail}` : ''} <span className="text-gray-500">by {f.userName || 'user'}</span></div>
                      ))}
                    </div>
                  )}
                  <div className="mt-3 flex gap-2 flex-wrap">
                    <button data-testid={`mod-approve-${it.id}`} onClick={() => approveListing(it.id)} disabled={busy === it.id + ':approve'}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-500 text-black text-xs font-semibold disabled:opacity-60">
                      {busy === it.id + ':approve' ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Approve
                    </button>
                    <button data-testid={`mod-reject-${it.id}`} onClick={() => rejectListing(it.id)} disabled={busy === it.id + ':reject'}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-500/50 text-red-400 hover:bg-red-500/10 text-xs disabled:opacity-60">
                      {busy === it.id + ':reject' ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />} Reject
                    </button>
                    <button data-testid={`mod-price-${it.id}`} onClick={() => overridePrice(it.id)} disabled={busy === it.id + ':price'}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[#2a2a2a] text-gray-300 hover:border-violet-500 hover:text-violet-300 text-xs disabled:opacity-60">
                      {busy === it.id + ':price' ? <Loader2 size={12} className="animate-spin" /> : <DollarSign size={12} />} Override price
                    </button>
                    <button data-testid={`mod-delivery-${it.id}`} onClick={() => overrideDelivery(it.id)} disabled={busy === it.id + ':delivery'}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[#2a2a2a] text-gray-300 hover:border-blue-500 hover:text-blue-300 text-xs disabled:opacity-60">
                      {busy === it.id + ':delivery' ? <Loader2 size={12} className="animate-spin" /> : <DollarSign size={12} />} Override delivery
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      ) : tab === 'auctions' ? (
        <AdminAuctionsPanel />
      ) : tab === 'tiers' ? (
        <div className="space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[240px] max-w-md">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input data-testid="admin-tier-search" value={bizQuery} onChange={(e) => setBizQuery(e.target.value)}
                placeholder="Search by business, category, owner email…"
                className="w-full bg-black border border-[#1f1f1f] rounded-lg pl-9 pr-3 py-2 text-sm" />
            </div>
            <button data-testid="admin-tier-refresh" onClick={searchBusinesses} disabled={busy === 'biz:search'}
              className="px-3 py-2 rounded-lg bg-green-600 hover:bg-green-500 text-black text-xs font-semibold disabled:opacity-60 inline-flex items-center gap-1">
              {busy === 'biz:search' ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />} Refresh
            </button>
            <button data-testid="admin-nudge-pending" onClick={nudgePending} disabled={busy === 'biz:nudge'}
              title="Send an onboarding reminder email to every business owner who hasn't created a listing yet (7-day cooldown)"
              className="px-3 py-2 rounded-lg border border-orange-500/40 text-orange-300 hover:bg-orange-500/10 text-xs font-semibold disabled:opacity-60 inline-flex items-center gap-1">
              {busy === 'biz:nudge' ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Nudge pending
            </button>
            <div className="text-xs text-gray-500 ml-auto">{filteredBusinesses.length} businesses</div>
          </div>

          {filteredBusinesses.length === 0 ? (
            <div className="pbb-card p-10 text-center">
              <Building2 className="mx-auto text-green-400" size={26} />
              <div className="mt-3 font-semibold">No businesses found</div>
              <p className="text-sm text-gray-400 mt-1">Try a different search or add businesses via signup.</p>
            </div>
          ) : (
            <div className="pbb-card overflow-hidden">
              {/* Bulk action banner — only appears when at least one row is checked. */}
              {bulkSelected.size > 0 && (
                <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-red-500/10 border-b border-red-500/30"
                     data-testid="admin-bulk-bar">
                  <div className="text-sm text-red-200">
                    <span className="font-semibold" data-testid="admin-bulk-count">{bulkSelected.size}</span>{' '}
                    {bulkSelected.size === 1 ? 'listing' : 'listings'} selected
                  </div>
                  <div className="flex items-center gap-2">
                    <button data-testid="admin-bulk-clear" onClick={() => setBulkSelected(new Set())}
                            className="px-3 py-1.5 rounded-lg border border-[#2a2a2a] text-gray-300 hover:text-white text-xs">
                      Clear
                    </button>
                    <button data-testid="admin-bulk-delete-open"
                            onClick={() => {
                              const count = [...bulkSelected].filter((id) => !id.startsWith('pending-')).length;
                              if (!count) { toast({ title: 'No deletable rows selected' }); return; }
                              setBulkDeleteModal({ count, typed: '', progress: 0 });
                            }}
                            className="px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-500 text-white text-xs font-semibold inline-flex items-center gap-1">
                      <Trash2 size={12}/> Delete {bulkSelected.size} selected
                    </button>
                  </div>
                </div>
              )}
              <table className="w-full text-sm">
                <thead className="bg-[#0d1f14] text-left text-xs text-gray-400 uppercase">
                  <tr>
                    <th className="pl-4 pr-1 py-3 w-8">
                      <input type="checkbox"
                             data-testid="admin-bulk-select-all"
                             aria-label="Select all listings"
                             checked={allEligibleSelected()}
                             onChange={toggleSelectAll}
                             className="cursor-pointer"/>
                    </th>
                    <th className="px-4 py-3">Business</th>
                    <th className="px-4 py-3">Owner</th>
                    <th className="px-4 py-3">Current package</th>
                    <th className="px-4 py-3">Expires</th>
                    <th className="px-4 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBusinesses.map((b) => (
                    <tr key={b.id} data-testid={b.pendingOwner ? `admin-pending-owner-${b.ownerUserId}` : `admin-biz-${b.id}`}
                      className={`border-t border-[#1f1f1f] ${b.pendingOwner ? 'bg-orange-500/[0.04]' : ''} ${bulkSelected.has(b.id) ? 'bg-red-500/[0.04]' : ''}`}>
                      <td className="pl-4 pr-1 py-3 align-top">
                        {b.pendingOwner ? (
                          // Pending owners aren't real listings — no checkbox.
                          <span className="text-gray-700 text-xs" title="Pending owner — no listing to delete">—</span>
                        ) : (
                          <input type="checkbox"
                                 data-testid={`admin-bulk-select-${b.id}`}
                                 aria-label={`Select ${b.name}`}
                                 checked={bulkSelected.has(b.id)}
                                 onChange={() => toggleOne(b.id)}
                                 className="cursor-pointer mt-1"/>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-white flex items-center gap-2">
                          {b.name}
                          {b.pendingOwner && (
                            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-orange-400/40 text-orange-300 bg-orange-500/10">
                              No listing yet
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">
                          {b.pendingOwner
                            ? 'Signed up as Business Owner · has not created a listing'
                            : `${b.category || '—'} · ${b.city || '—'}`}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-300">
                        <div>{b.ownerName || '—'}</div>
                        <div className="text-xs text-gray-500">{b.ownerEmail || '—'}</div>
                      </td>
                      <td className="px-4 py-3">
                        {b.pendingOwner ? (
                          <span className="text-[11px] uppercase tracking-wider text-gray-500">—</span>
                        ) : (
                          <span className="text-[11px] uppercase tracking-wider px-2 py-0.5 rounded border border-green-500/40 text-green-300 bg-green-500/10">
                            {b.package || 'none'}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-400">
                        {b.pendingOwner
                          ? (b.createdAt ? `Signed up ${new Date(b.createdAt).toLocaleDateString()}` : '—')
                          : (b.packageExpiresAt ? new Date(b.packageExpiresAt).toLocaleDateString() : '—')}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-2 flex-wrap justify-end">
                          {b.pendingOwner ? (
                            <button data-testid={`admin-create-listing-for-${b.ownerUserId}`}
                              onClick={() => setEditBizModal({
                                pendingOwnerId: b.ownerUserId,
                                name: '', category: '', city: '', description: '',
                                phone: b.ownerPhone || '', email: b.ownerEmail || '',
                              })}
                              className="px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-500 text-black text-xs font-semibold inline-flex items-center gap-1">
                              <Building2 size={12} /> Create listing
                            </button>
                          ) : (
                            <>
                              <button data-testid={`admin-biz-edit-${b.id}`}
                                onClick={() => setEditBizModal({
                                  ...b,
                                  packageExpiresAt: b.packageExpiresAt ? b.packageExpiresAt.slice(0,10) : '',
                                })}
                                className="px-3 py-1.5 rounded-lg border border-[#2a2a2a] hover:border-green-500 text-xs font-semibold inline-flex items-center gap-1">
                                <Edit3 size={12} /> Edit
                              </button>
                              <button data-testid={`admin-tier-grant-${b.id}`} onClick={() => setTierModal({ biz: b, package: 'standard', months: 3 })}
                                className="px-3 py-1.5 rounded-lg bg-yellow-500 hover:bg-yellow-400 text-black text-xs font-semibold inline-flex items-center gap-1">
                                <Crown size={12} /> Grant tier
                              </button>
                              <button data-testid={`admin-transfer-${b.id}`}
                                onClick={() => setTransferModal({ biz: b, email: '', matches: [], chosen: null })}
                                className="px-3 py-1.5 rounded-lg border border-blue-500/50 text-blue-300 hover:bg-blue-500/10 text-xs font-semibold inline-flex items-center gap-1">
                                <Repeat2 size={12} /> Transfer
                              </button>
                              <button data-testid={`admin-biz-delete-${b.id}`}
                                onClick={() => setDeleteBizModal({ biz: b, typed: '' })}
                                className="px-3 py-1.5 rounded-lg border border-red-500/60 text-red-300 hover:bg-red-500/10 text-xs font-semibold inline-flex items-center gap-1">
                                <Trash2 size={12} /> Delete
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : tab === 'users' ? (
        <AdminUsersPanel />
      ) : tab === 'site-editor' ? (
        <AdminSiteEditorPanel />
      ) : tab === 'popup' ? (
        <AdminPopupPanel />
      ) : tab === 'blackout' ? (
        <AdminBlackoutPanel />
      ) : tab === 'plan-prices' ? (
        <AdminPlanPricesPanel />
      ) : tab === 'sales' ? (
        <AdminSalesPanel />
      ) : tab === 'contacts' ? (
        <AdminContactsPanel />
      ) : tab === 'welcome' ? (
        <AdminWelcomeEditor />
      ) : tab === 'reports' ? (
        <AdminReportsPanel />
      ) : tab === 'slider-forms' ? (
        <AdminSliderFormsPanel />
      ) : tab === 'events' ? (
        <AdminContentPanel schema={{
          ...EVENTS_SCHEMA,
          api: {
            list: (q) => admin.listEvents(q),
            create: (payload) => admin.createEvent(payload),
            update: (id, payload) => admin.updateEvent(id, payload),
            remove: (id) => admin.deleteEvent(id),
          },
        }} />
      ) : tab === 'dating' ? (
        <AdminContentPanel schema={{
          ...DATING_SCHEMA,
          api: {
            list: (q) => admin.listDating(q),
            create: (payload) => admin.createDating(payload),
            update: (id, payload) => admin.updateDating(id, payload),
            remove: (id) => admin.deleteDating(id),
          },
        }} />
      ) : tab === 'newsletter' ? (
        <AdminNewsletterPanel key="nl" />
      ) : tab === 'newsletter-contacts' ? (
        <AdminNewsletterPanel key="nlc" initialTab="contacts" />
      ) : tab === 'community-reports' ? (
        <AdminCommunityReportsPanel />
      ) : tab === 'dating-reports' ? (
        <AdminDatingReportsPanel />
      ) : tab === 'dating-verifications' ? (
        <AdminDatingVerificationsPanel />
      ) : tab === 'news' ? (
        <div className="space-y-6">
        <AdminNewsSourcesPanel />
        <div className="text-sm font-semibold text-gray-300">Pinned articles (shown at the top of the News Feed)</div>
        <AdminContentPanel schema={{
          ...NEWS_SCHEMA,
          api: {
            list: (q) => admin.listNews(q),
            create: (payload) => admin.createNews(payload),
            update: (id, payload) => admin.updateNews(id, payload),
            remove: (id) => admin.deleteNews(id),
          },
        }} />
        </div>
      ) : tab === 'psa' ? (
        <AdminPsaPanel />
      ) : tab === 'frozen' ? (
        rows.length === 0 ? (
          <div className="pbb-card p-10 text-center">
            <ShieldAlert className="mx-auto text-green-400" size={26} />
            <div className="mt-3 font-semibold">No frozen users</div>
            <p className="text-sm text-gray-400 mt-1">The community is behaving. Nice.</p>
          </div>
        ) : (
          <div className="pbb-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#0d1f14] text-left text-xs text-gray-400 uppercase">
                <tr>
                  <th className="px-4 py-3">User</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Freeze started</th>
                  <th className="px-4 py-3">Ends</th>
                  <th className="px-4 py-3">Reason</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => (
                  <tr key={u.id} className="border-t border-[#1f1f1f]">
                    <td className="px-4 py-3 flex items-center gap-2">
                      <img src={filesApi.urlFor(u.avatar)} alt="" className="w-8 h-8 rounded-full object-cover" /> {u.name}
                    </td>
                    <td className="px-4 py-3 text-gray-300"><Mail size={12} className="inline mr-1 text-gray-500" />{u.email}</td>
                    <td className="px-4 py-3 text-gray-400"><Calendar size={12} className="inline mr-1" />{u.frozenStartedAt ? new Date(u.frozenStartedAt).toLocaleDateString() : '—'}</td>
                    <td className="px-4 py-3 text-gray-400">{new Date(u.reviewsFrozenUntil).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-xs text-gray-400">{u.frozenReason || 'review_rating_cap'}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => unfreeze(u.id)} disabled={busy === u.id + ':unfreeze'} className="px-3 py-1.5 rounded-lg border border-green-600/40 text-green-400 hover:bg-green-600/10 text-xs inline-flex items-center gap-1 disabled:opacity-60">
                          {busy === u.id + ':unfreeze' ? <Loader2 size={12} className="animate-spin" /> : <UnlockIcon size={12} />} Unfreeze
                        </button>
                        <button onClick={() => extend(u.id)} disabled={busy === u.id + ':extend'} className="px-3 py-1.5 rounded-lg border border-[#2a2a2a] hover:border-yellow-500 hover:text-yellow-400 text-xs inline-flex items-center gap-1 disabled:opacity-60">
                          {busy === u.id + ':extend' ? <Loader2 size={12} className="animate-spin" /> : '+'} Extend
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}

      {tierModal && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center px-4">
          <div className="w-full max-w-md bg-[#0f0f0f] border border-[#1f1f1f] rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#1f1f1f]">
              <div className="font-semibold flex items-center gap-2"><Crown size={16} className="text-yellow-400" /> Grant tier</div>
              <button onClick={() => setTierModal(null)} className="text-gray-400 hover:text-white"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Business</div>
                <div className="text-white font-medium">{tierModal.biz?.name}</div>
                <div className="text-xs text-gray-500">Current: {tierModal.biz?.package || 'none'}
                  {tierModal.biz?.packageExpiresAt ? ` · expires ${new Date(tierModal.biz.packageExpiresAt).toLocaleDateString()}` : ''}
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Package</label>
                <select data-testid="tier-package" value={tierModal.package}
                  onChange={(e) => {
                    const pkg = e.target.value;
                    const found = PACKAGE_CHOICES.find((p) => p.key === pkg);
                    setTierModal({ ...tierModal, package: pkg, months: found?.months ?? tierModal.months });
                  }}
                  className="mt-1 w-full bg-black border border-[#1f1f1f] rounded-lg px-3 py-2 text-sm">
                  {PACKAGE_CHOICES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Duration (months)</label>
                <input data-testid="tier-months" type="number" min="1" step="1" value={tierModal.months || 1}
                  onChange={(e) => setTierModal({ ...tierModal, months: parseInt(e.target.value || '1', 10) })}
                  className="mt-1 w-full bg-black border border-[#1f1f1f] rounded-lg px-3 py-2 text-sm" />
                <div className="text-[11px] text-gray-500 mt-1">This is a free comp — the business is granted the tier without a PayPal charge.</div>
              </div>
            </div>
            <div className="px-5 py-4 border-t border-[#1f1f1f] flex justify-end gap-2">
              <button onClick={() => setTierModal(null)}
                className="px-3 py-2 rounded-lg border border-[#2a2a2a] text-gray-300 hover:text-white text-sm">
                Cancel
              </button>
              <button data-testid="tier-submit" onClick={submitTier} disabled={busy === 'tier:submit'}
                className="px-4 py-2 rounded-lg bg-yellow-500 hover:bg-yellow-400 text-black text-sm font-semibold disabled:opacity-60 inline-flex items-center gap-1">
                {busy === 'tier:submit' ? <Loader2 size={12} className="animate-spin" /> : <Crown size={12} />} Grant
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation — type-to-confirm dialog so no listing gets nuked by accident. */}
      {deleteBizModal && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center px-4"
             data-testid="admin-biz-delete-modal">
          <div className="w-full max-w-md bg-[#0f0f0f] border border-red-500/40 rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-red-500/30">
              <div className="font-semibold flex items-center gap-2 text-red-300">
                <Trash2 size={16}/> Delete listing
              </div>
              <button onClick={() => setDeleteBizModal(null)} className="text-gray-400 hover:text-white"><X size={18}/></button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <p className="text-gray-300">
                You're about to permanently delete
                <span className="font-semibold text-white"> "{deleteBizModal.biz?.name}"</span>.
                This cannot be undone.
              </p>
              <label className="block">
                <span className="text-xs text-gray-400">Type the business name to confirm</span>
                <input data-testid="admin-biz-delete-typed"
                       value={deleteBizModal.typed || ''}
                       onChange={(e) => setDeleteBizModal({ ...deleteBizModal, typed: e.target.value })}
                       autoFocus
                       placeholder={deleteBizModal.biz?.name}
                       className="mt-1 w-full bg-black border border-[#2a2a2a] rounded-lg px-3 py-2 text-sm"/>
              </label>
            </div>
            <div className="px-5 py-4 border-t border-[#1f1f1f] flex justify-end gap-2">
              <button onClick={() => setDeleteBizModal(null)}
                      className="px-3 py-2 rounded-lg border border-[#2a2a2a] text-gray-300 hover:text-white text-sm">
                Cancel
              </button>
              <button data-testid="admin-biz-delete-submit" onClick={submitDeleteBiz}
                      disabled={busy === 'delbiz:submit'
                                  || (deleteBizModal.typed || '').trim() !== (deleteBizModal.biz?.name || '').trim()}
                      className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm font-semibold disabled:opacity-40 inline-flex items-center gap-1">
                {busy === 'delbiz:submit' ? <Loader2 size={12} className="animate-spin"/> : <Trash2 size={12}/>} Delete permanently
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk delete — type "DELETE N" to confirm, then rows are removed one
          at a time with a progress counter. Pending-owner placeholders are
          filtered out of the count. */}
      {bulkDeleteModal && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center px-4"
             data-testid="admin-bulk-delete-modal">
          <div className="w-full max-w-md bg-[#0f0f0f] border border-red-500/40 rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-red-500/30">
              <div className="font-semibold flex items-center gap-2 text-red-300">
                <Trash2 size={16}/> Bulk delete {bulkDeleteModal.count} listings
              </div>
              <button onClick={() => setBulkDeleteModal(null)} className="text-gray-400 hover:text-white"><X size={18}/></button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <p className="text-gray-300">
                You're about to permanently delete
                <span className="font-semibold text-white"> {bulkDeleteModal.count} listing{bulkDeleteModal.count !== 1 ? 's' : ''}</span>.
                This cannot be undone.
              </p>
              <label className="block">
                <span className="text-xs text-gray-400">
                  Type <code className="text-red-300 font-semibold">DELETE {bulkDeleteModal.count}</code> to confirm
                </span>
                <input data-testid="admin-bulk-delete-typed"
                       value={bulkDeleteModal.typed || ''}
                       onChange={(e) => setBulkDeleteModal({ ...bulkDeleteModal, typed: e.target.value })}
                       autoFocus
                       placeholder={`DELETE ${bulkDeleteModal.count}`}
                       className="mt-1 w-full bg-black border border-[#2a2a2a] rounded-lg px-3 py-2 text-sm font-mono"/>
              </label>
              {busy === 'bulkdel:submit' && (
                <div className="text-xs text-gray-400" data-testid="admin-bulk-delete-progress">
                  Deleting… {bulkDeleteModal.progress || 0} / {bulkDeleteModal.count}
                </div>
              )}
            </div>
            <div className="px-5 py-4 border-t border-[#1f1f1f] flex justify-end gap-2">
              <button onClick={() => setBulkDeleteModal(null)} disabled={busy === 'bulkdel:submit'}
                      className="px-3 py-2 rounded-lg border border-[#2a2a2a] text-gray-300 hover:text-white text-sm disabled:opacity-50">
                Cancel
              </button>
              <button data-testid="admin-bulk-delete-submit"
                      onClick={submitBulkDelete}
                      disabled={busy === 'bulkdel:submit'
                                  || (bulkDeleteModal.typed || '').trim().toUpperCase() !== `DELETE ${bulkDeleteModal.count}`}
                      className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm font-semibold disabled:opacity-40 inline-flex items-center gap-1">
                {busy === 'bulkdel:submit' ? <Loader2 size={12} className="animate-spin"/> : <Trash2 size={12}/>}
                Delete {bulkDeleteModal.count} permanently
              </button>
            </div>
          </div>
        </div>
      )}

      {transferModal && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center px-4">
          <div className="w-full max-w-md bg-[#0f0f0f] border border-[#1f1f1f] rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#1f1f1f]">
              <div className="font-semibold flex items-center gap-2"><Repeat2 size={16} className="text-blue-400" /> Transfer ownership</div>
              <button onClick={() => setTransferModal(null)} className="text-gray-400 hover:text-white"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Business</div>
                <div className="text-white font-medium">{transferModal.biz?.name}</div>
                <div className="text-xs text-gray-500">
                  Current owner: {transferModal.biz?.ownerEmail || '—'} ({transferModal.biz?.ownerName || '—'})
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">New owner email</label>
                <div className="flex gap-2 mt-1">
                  <input data-testid="transfer-email" type="email" value={transferModal.email}
                    onChange={(e) => setTransferModal({ ...transferModal, email: e.target.value, matches: [], chosen: null })}
                    onKeyDown={(e) => { if (e.key === 'Enter') lookupTransferUser(); }}
                    placeholder="owner@example.com"
                    className="flex-1 bg-black border border-[#1f1f1f] rounded-lg px-3 py-2 text-sm" />
                  <button data-testid="transfer-lookup" onClick={lookupTransferUser} disabled={busy === 'transfer:lookup'}
                    className="px-3 py-2 rounded-lg bg-blue-500 hover:bg-blue-400 text-white text-xs font-semibold disabled:opacity-60 inline-flex items-center gap-1">
                    {busy === 'transfer:lookup' ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />} Look up
                  </button>
                </div>
                <div className="text-[11px] text-gray-500 mt-1">User must already be registered.</div>
              </div>

              {transferModal.matches?.length > 0 && (
                <div className="border border-[#1f1f1f] rounded-lg divide-y divide-[#1f1f1f]" data-testid="transfer-matches">
                  {transferModal.matches.map((u) => (
                    <button key={u.id} type="button"
                      onClick={() => setTransferModal({ ...transferModal, chosen: u })}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-[#151515] ${transferModal.chosen?.id === u.id ? 'bg-blue-500/10' : ''}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <div className="text-white">{u.name || '(no name)'} <span className="text-gray-500 text-xs">· {u.email}</span></div>
                          <div className="text-[10px] text-gray-500">
                            {u.isAdmin ? 'admin · ' : ''}{u.isBusinessOwner ? 'business owner' : 'regular user (will be promoted)'}
                          </div>
                        </div>
                        {transferModal.chosen?.id === u.id && <Check size={14} className="text-green-400" />}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="px-5 py-4 border-t border-[#1f1f1f] flex justify-end gap-2">
              <button onClick={() => setTransferModal(null)}
                className="px-3 py-2 rounded-lg border border-[#2a2a2a] text-gray-300 hover:text-white text-sm">
                Cancel
              </button>
              <button data-testid="transfer-submit" onClick={submitTransfer}
                disabled={busy === 'transfer:submit' || !transferModal.chosen}
                className="px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-400 text-white text-sm font-semibold disabled:opacity-60 inline-flex items-center gap-1">
                {busy === 'transfer:submit' ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Transfer
              </button>
            </div>
          </div>
        </div>
      )}

      {editBizModal && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center px-4">
          <div className="w-full max-w-2xl bg-[#0f0f0f] border border-[#1f1f1f] rounded-2xl overflow-hidden max-h-[92vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#1f1f1f]">
              <div className="font-semibold flex items-center gap-2"><Edit3 size={16} className="text-green-400"/> Edit business listing</div>
              <button onClick={() => setEditBizModal(null)} className="text-gray-400 hover:text-white"><X size={18}/></button>
            </div>
            <div className="p-5 space-y-3 overflow-y-auto text-sm">
              <div className="text-[11px] text-gray-500">Editing as admin · changes save immediately and reflect on the public directory.</div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Business name</label>
                <input data-testid="admin-editbiz-name" className="pbb-input mt-1" value={editBizModal.name || ''} onChange={e => setEditBizModal({ ...editBizModal, name: e.target.value })}/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Category</label>
                  <input data-testid="admin-editbiz-category" className="pbb-input mt-1" value={editBizModal.category || ''} onChange={e => setEditBizModal({ ...editBizModal, category: e.target.value })}/>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Tier / Package</label>
                  <select data-testid="admin-editbiz-package" className="pbb-input mt-1" value={editBizModal.package || 'basic'} onChange={e => setEditBizModal({ ...editBizModal, package: e.target.value })}>
                    <option value="basic">Basic — Category Listing</option>
                    <option value="standard">Standard — Business Growth</option>
                    <option value="featured">Featured — Full Automation</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Street address</label>
                <input data-testid="admin-editbiz-address" className="pbb-input mt-1" value={editBizModal.address || ''} onChange={e => setEditBizModal({ ...editBizModal, address: e.target.value })}/>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">City</label>
                  <input data-testid="admin-editbiz-city" className="pbb-input mt-1" value={editBizModal.city || ''} onChange={e => setEditBizModal({ ...editBizModal, city: e.target.value })}/>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">State</label>
                  <input data-testid="admin-editbiz-state" className="pbb-input mt-1" value={editBizModal.state || ''} onChange={e => setEditBizModal({ ...editBizModal, state: e.target.value.toUpperCase() })}/>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Zip</label>
                  <input data-testid="admin-editbiz-zip" className="pbb-input mt-1" value={editBizModal.zip || ''} onChange={e => setEditBizModal({ ...editBizModal, zip: e.target.value })}/>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Phone</label>
                  <input data-testid="admin-editbiz-phone" className="pbb-input mt-1" value={editBizModal.phone || ''} onChange={e => setEditBizModal({ ...editBizModal, phone: e.target.value })}/>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Email</label>
                  <input data-testid="admin-editbiz-email" className="pbb-input mt-1" value={editBizModal.email || ''} onChange={e => setEditBizModal({ ...editBizModal, email: e.target.value })}/>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Description</label>
                <textarea rows={4} data-testid="admin-editbiz-description" className="pbb-input mt-1" value={editBizModal.description || ''} onChange={e => setEditBizModal({ ...editBizModal, description: e.target.value })}/>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Coupon (short summary)</label>
                  <input data-testid="admin-editbiz-coupon" className="pbb-input mt-1" value={editBizModal.coupon || ''} onChange={e => setEditBizModal({ ...editBizModal, coupon: e.target.value })}/>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Points</label>
                  <input type="number" data-testid="admin-editbiz-points" className="pbb-input mt-1" value={editBizModal.points ?? 0} onChange={e => setEditBizModal({ ...editBizModal, points: e.target.value })}/>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Package expiry (YYYY-MM-DD)</label>
                <input type="date" data-testid="admin-editbiz-expires" className="pbb-input mt-1" value={editBizModal.packageExpiresAt || ''} onChange={e => setEditBizModal({ ...editBizModal, packageExpiresAt: e.target.value })}/>
              </div>
            </div>
            <div className="px-5 py-4 border-t border-[#1f1f1f] flex justify-end gap-2">
              <button onClick={() => setEditBizModal(null)} className="px-3 py-2 rounded-lg border border-[#2a2a2a] text-gray-300 hover:text-white text-sm">Cancel</button>
              <button data-testid="admin-editbiz-submit" onClick={submitEditBiz} disabled={busy === 'editbiz:submit'}
                className="px-4 py-2 rounded-lg bg-green-600 hover:bg-green-500 text-white text-sm font-semibold disabled:opacity-60 inline-flex items-center gap-1">
                {busy === 'editbiz:submit' ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
